import React, {
  useState,
  useEffect,
  useMemo,
  useContext, useRef, FC
} from "react";
import { ReactImageGalleryItem } from "react-image-gallery";
import { Property } from "../../models/Property";
import { AccessibilityUnit } from "../../models/AccessibilityUnits";
import { PropertyAccessibilityImages } from "../../models/PropertyAccessibilityImages";
import { ImageGalleryRenderer } from "../../components/image-gallery/ImageGalleryRenderer";
import { A11yStandardType } from "../../models/A11yStandard";
import {a11yStandardKeys, METRIC_TRIGGERED_ON, unitTypeKeys} from "../../constants/constants";
import {
  BUILDING_INFO_TAB,
  getUnitListTab,
  PropertyPageTabKey,
  SHOPS_TAB,
  UNIT_LIST_TABS,
} from "../../constants/propertyPageTabs";
import { AccessibilityWidgetInfoContainer } from "../../components/AccessibilityWidgetInfoContainer";
import {TabsContext} from "../../contexts/tabsContext";
import {PropertyPageTabContext} from "../../contexts/propertyPageTabContext";
import { Mixpanel } from "../../analytics/Mixpanel";
import { MetricMessage } from "../../analytics/metrics";
import styled from "styled-components";
import { useFetchInfoFromProperty } from "../../hooks/useFetchInfoFromProperty";
import ImageContainer from "../../components/image-gallery/ImageContainer";
import GeneralRoomInformationContainer from "./components/GeneralRoomInformationContainer";
import InformationBlock from "./components/InformationBlock";
import ElementOfTable from "./components/ElementOfTable";
import TabContentProperties from "./components/TabContentProperties";
import TabContentUnits from "./components/TabContentUnits";
import TabsBlock from "./components/TabsBlock";
import {MapContext} from "../../contexts/mapContext";
import MapWrapper from "../../components/map/MapWrapper";
import {useStorageData} from "../../hooks/useStorageData";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import InformationByDisability from "../../components/InformationByDisabilityContainer";
import {Button, useMediaQuery} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import {appendUtmParams} from "../../utils/url";
import {getFilteredPropertyImagesByDisabilityType, getPropertyGeneralImage, sortPropertyImages} from "../../utils/images";
import {getTextByCurrentLanguage} from "../../utils/language";
import {scrollToUnitByCurrentImage} from "../../utils/dom";
import {useTranslation} from "react-i18next";
import MainAccessibility from "../../components/MainAccessibility";
import {Box} from "@mui/system";

const InformationWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const RootDiv = styled.div`
  display: flex;
  flex-direction: column;
`;

type TypeProps = {
  property: Property;
  a11yStandard: A11yStandardType;
  displayShops: boolean;
  companyIdAPIKey: string;
  buildingAreaId: string | null;
  fullscreen: boolean
}

export const PropertyPage:FC<TypeProps> = ({ property, displayShops, buildingAreaId, fullscreen, a11yStandard: a11yStandardProps }) => {
  const [field] = useContext(TabsContext);
  const [savedTab, saveTab] = useContext(PropertyPageTabContext);
  // the page remounts when the widget reloads its data, it then opens the tab it had
  const [tab, setTab] = useState<PropertyPageTabKey>(
    savedTab && savedTab.field === field ? savedTab.tab : BUILDING_INFO_TAB
  );
  const theme = useTheme()
  const [accessibilityTable, setAccessibilityTable] = useState<JSX.Element[]>();
  const [activePoiId, setActivePoiId] = useState("");
  const [clickedMapPoiName, setClickedMapPoiName] = useState("");
  const [showMap] = useContext(MapContext);
  const [currentImageData, setCurrentImageData] = useState({index: 0, accessibility_unit: '', name: ''})
  const disabilityTypes = useStorageData('disability_types')
  const { height } = useWindowDimensions();
  const generalInformationRefs = useRef<HTMLDivElement[] | null[]>([])
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { t } = useTranslation()
  const [informationBlockStatusData, setInformationBlockStatusData] = useState<boolean>(false)

  useEffect(() => {
    // another property opens on its building information
    if (!savedTab || savedTab.field !== field) {
      setTab(BUILDING_INFO_TAB);
      saveTab({field, tab: BUILDING_INFO_TAB});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field])

  
  const displayUnitRooms = (rooms: AccessibilityUnit[]) =>
  !!(rooms && rooms.length > 0) && buildingAreaId === null
  
  const {
    parkingSpaces,
    toiletRoom,
    totalShop,
    accessibilities,
    hospitalityRooms,
    meetingRooms,
    exhibitionRooms,
    museumShopRooms,
    toiletCommonRooms,
    propertyImages,
    propertyAccessibilityInfo,
    propertyFreeTextInfo,
    accessibleUnits,
    unitLevels,
    questions,
    a11yStandard,
    attractions,
    restaurants,
    mannedKiosks,
  } = useFetchInfoFromProperty(property, a11yStandardProps);

  // the units of every unit list tab
  const unitsByType: Record<string, AccessibilityUnit[]> = {
    [unitTypeKeys.hotelRoomType]: hospitalityRooms,
    [unitTypeKeys.meetingRoom]: meetingRooms,
    [unitTypeKeys.exhibition]: exhibitionRooms,
    [unitTypeKeys.museumShop]: museumShopRooms,
    [unitTypeKeys.attraction]: attractions,
    [unitTypeKeys.restaurant]: restaurants,
    [unitTypeKeys.mannedKiosk]: mannedKiosks,
    [unitTypeKeys.toiletRoomCommon]: toiletCommonRooms,
  }
  const openUnitList = getUnitListTab(tab)

  const trackTab = (newTab: PropertyPageTabKey) => {
    if (newTab === BUILDING_INFO_TAB) {
      Mixpanel.track(`${MetricMessage.accessed_property_tab}`);
    } else if (newTab === SHOPS_TAB) {
      Mixpanel.track(`${MetricMessage.accessed_unit_list}`, {
        a11yStandard: a11yStandardKeys.shop,
        unitType: null
      });
    } else {
      Mixpanel.track(`${MetricMessage.accessed_unit_list}`, {
        a11yStandard: null,
        unitType: newTab
      });
    }
  }

  const handleChange = (newTab: PropertyPageTabKey) => {
    trackTab(newTab);
    saveTab({field, tab: newTab});
    setTab(newTab);
  };

  useEffect(() => {
    if (propertyImages && propertyImages.length > 0) {
      handlerCurrentImage(0, "")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyImages, tab])

  useEffect(() => {
    if (accessibleUnits && accessibleUnits.length > 0) {
      const table = accessibleUnits.map((el, index) => (
        <ElementOfTable
          id={el.id}
          name={getTextByCurrentLanguage(el.name)}
          title={el.title}
          key={el.id}
          onSetRef={el => generalInformationRefs.current[index] = el}
          accessibilities={accessibilities}
        />
      ));
      setAccessibilityTable(table);
    }
  }, [accessibleUnits, accessibilities]);

  const carouselImages: ReactImageGalleryItem[] = useMemo(() => {
    if (showMap && activePoiId && clickedMapPoiName) {
      let poiImages: PropertyAccessibilityImages[] = [];
      if (propertyImages.length > 0) {
        const sortedPropertyImages = sortPropertyImages(propertyImages, accessibleUnits)
        let filteredPropertyImages = getFilteredPropertyImagesByDisabilityType(sortedPropertyImages, disabilityTypes, accessibilities)
        const filteredByAccessibilityUnit = filteredPropertyImages
          .filter((i) => i.accessibility_unit === activePoiId)
        if (filteredByAccessibilityUnit.length > 0) {
          poiImages = filteredByAccessibilityUnit
        }
      }

      return poiImages.map<ReactImageGalleryItem>(
        ({ thumbnails, description, accessibility_unit }) => ({
          fullscreen: thumbnails.large,
          original: thumbnails.medium,
          thumbnail: thumbnails.small,
          renderItem: ImageGalleryRenderer(),
          thumbnailClass: "handiscover-thumbnail-class",
          imageDescription: description,
          description: clickedMapPoiName
        })
      );
    } else if (propertyImages && accessibleUnits) {
      const sortedPropertyImages = sortPropertyImages(propertyImages, accessibleUnits)
      let filteredPropertyImages = getFilteredPropertyImagesByDisabilityType(sortedPropertyImages, disabilityTypes, accessibilities)
      const foundGeneralImage = filteredPropertyImages.find((i) => i.id === property.id)
      if (property.general_image && !foundGeneralImage) {
        filteredPropertyImages.unshift({
          id: property.id,
          thumbnails: {
            small: getPropertyGeneralImage(property, 'small'),
            medium: getPropertyGeneralImage(property, 'medium'),
            large: property.general_image
          },
          image: property.general_image,
          is_display_in_output_service: true,
          is_general_image: false,
          accessibility_unit: '',
          accessibility: '',
          description: property.general_image_description,
        })
      }
      return filteredPropertyImages.map<ReactImageGalleryItem>(
        ({ thumbnails, description, accessibility_unit }) => ({
          fullscreen: thumbnails.large,
          original: thumbnails.medium,
          thumbnail: thumbnails.small,
          renderItem: ImageGalleryRenderer(),
          thumbnailClass: "handiscover-thumbnail-class",
          imageDescription: description,
          description: getTextByCurrentLanguage(accessibleUnits?.find((el) => el.id ===  accessibility_unit)?.name),
        })
      );
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyImages, showMap, activePoiId, disabilityTypes, accessibilities, accessibleUnits]);

  useEffect(() => {
    if (activePoiId.length > 0) {
      if (showMap) {
        handlerCurrentImage(0, METRIC_TRIGGERED_ON.PROPERTY_PAGE_MAP_VIEW)
      } else {
        setActivePoiId('')
        handlerCurrentImage(0, '')
      }
    }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMap, activePoiId])

  useEffect(() => {
    if (isDesktop && generalInformationRefs.current.length > 0) {
      scrollToUnitByCurrentImage(
        generalInformationRefs,
        currentImageData,
        'detailed-information'
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generalInformationRefs.current.length, currentImageData, accessibilityTable])

  const handlerCurrentImage = (currentIndex: number, navigatedOn: string) => {
    if (property.general_image && !showMap) {
      currentIndex = currentIndex - 1
    }
    const sortedPropertyImages = sortPropertyImages(propertyImages, accessibleUnits)
    if (sortedPropertyImages.length > 0) {
      let filteredPropertyImages = getFilteredPropertyImagesByDisabilityType(sortedPropertyImages, disabilityTypes, accessibilities)
      if (activePoiId) {
        filteredPropertyImages = filteredPropertyImages
          .filter((i) => i.accessibility_unit === activePoiId)
      }

      if (filteredPropertyImages.length > 0 && filteredPropertyImages[currentIndex]) {
        const description = accessibleUnits?.find((el) => (
          el.id === filteredPropertyImages[currentIndex].accessibility_unit
        ))
        setCurrentImageData({
          index: currentIndex,
          accessibility_unit: filteredPropertyImages[currentIndex].accessibility_unit,
          name: description ? getTextByCurrentLanguage(description.name) : ''
        })
      }
      if (navigatedOn !== "") {
        Mixpanel.track(`${MetricMessage.picture_navigation}`, {
          triggeredOn: navigatedOn
        });
      }
    }
  }

  const getBookButton = (property: Property, isDesktop: boolean, position: number) => {
    const { additional_information } = property
    if (additional_information && additional_information.link && tab === BUILDING_INFO_TAB) {
      const handleClick = () => {
        Mixpanel.track(`${MetricMessage.clicked_book_now}`);
        window.open(appendUtmParams(additional_information.link), '_blank');
      };

      if (position === 1) {
        return !isDesktop ? (
          <Button
            variant={'contained'}
            size={'small'}
            style={{marginBottom: 10, marginTop: 15, height: 30}}
            onClick={handleClick}

          >
            {t('book_now')}
          </Button>
        ) : null
      } else if (position === 2) {
        return isDesktop ? (
          <Button
            variant={'contained'}
            style={{maxHeight: 27, minWidth: 120}}
            onClick={handleClick}
          >
            {t('book_now')}
          </Button>
        ) : null
      }
    }
    return null
  }

  return (
    <RootDiv>
      <InformationBlock
        a11yStandard={a11yStandard}
        property={property}
        totalShop={totalShop}
        toiletRoom={toiletRoom}
        parkingSpaces={parkingSpaces}
        units={accessibleUnits}
        onGetStatusData={(status) => setInformationBlockStatusData(status)}
        onSetTab={handleChange}
      />
      {getBookButton(property, isDesktop, 1)}
      <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
        <TabsBlock
          value={tab}
          handleChange={handleChange}
          display={displayShops}
          a11yStandard={a11yStandard}
          accessibleUnits={accessibleUnits}
        />
        {getBookButton(property, isDesktop, 2)}
      </Box>
      {tab === BUILDING_INFO_TAB && (
        <AccessibilityWidgetInfoContainer
          height={height}
          fullscreen={fullscreen}
          displayShops={displayShops || UNIT_LIST_TABS.some(({unitType}) => displayUnitRooms(unitsByType[unitType]))}
          buildingAreaId={buildingAreaId}
          showMap={showMap}
          a11yStandard={a11yStandard}
          isBuildingArea={false}
          informationBlockStatusData={informationBlockStatusData}
        >
          {showMap ? null :
            <ImageContainer
              carouselImages={carouselImages}
              onSlide={(currentIndex) => handlerCurrentImage(
                currentIndex,
                METRIC_TRIGGERED_ON.PROPERTY_PAGE_BUILDING_INFO
              )}
            />
          }
          {(showMap && activePoiId) ? (
            <ImageContainer
              showCloseButton={true}
              onClose={() => setActivePoiId('')}
              propertyImages={propertyImages}
              carouselImages={carouselImages}
              onSlide={(currentIndex) => handlerCurrentImage(
                currentIndex,
                METRIC_TRIGGERED_ON.PROPERTY_PAGE_MAP_VIEW
              )}
              children={
                (carouselImages.length > 0) ? (
                  <MainAccessibility
                    title={''}
                    fullHeight={false}
                    currentImageData={currentImageData}
                    accessibilities={accessibilities}
                    a11yStandard={a11yStandard}
                    propertyItemAccessibilityUnits={accessibleUnits}
                    disabilityTypes={disabilityTypes}
                    marginBottom={false}
                    borderBottom={false}
                    fullscreen={fullscreen}
                    showOnlySelected={true}
                  />
                ) : (<React.Fragment/>)
              }
            />
            )
            :
            <InformationWrapper>
              <MainAccessibility
                title={t('general_accessibility_and_information')}
                currentImageData={currentImageData}
                accessibilities={accessibilities}
                a11yStandard={a11yStandard}
                propertyItemAccessibilityUnits={accessibleUnits}
                disabilityTypes={disabilityTypes}
              />
              <InformationByDisability
                questions={questions}
                accessibilityInfo={propertyAccessibilityInfo}
                freeTextInfo={propertyFreeTextInfo}
              />
            </InformationWrapper>
          }
          {(showMap && property.point && property.point.coordinates) ?
            <MapWrapper
              id={property.id}
              fullscreen={fullscreen}
              standard={property.a11y_standard}
              mapCenterCoordinates={property.point.coordinates}
              unitLevels={unitLevels}
              accessibleUnits={accessibleUnits}
              activePoiId={activePoiId}
              onSetActivePoiId={setActivePoiId}
              setClickedMapPoiName={setClickedMapPoiName}
              accessibilities={accessibilities}
            />
            :
            <GeneralRoomInformationContainer
              a11yStandard={a11yStandard}
              accessibilityTable={accessibilityTable}
              locatedOn={{
                triggeredOnPage: METRIC_TRIGGERED_ON.PROPERTY_PAGE_BUILDING_INFO,
                section: METRIC_TRIGGERED_ON.GENERAL_INFO_TABLE
              }}
            />
          }
        </AccessibilityWidgetInfoContainer>
      )}
      {tab === SHOPS_TAB && (
        <TabContentProperties
          a11yStandard={a11yStandard}
          property={property}
          fullscreen={fullscreen}
          displayShops={displayShops}
          buildingAreaId={buildingAreaId}
        />
      )}
      {openUnitList && (
        <TabContentUnits
          key={openUnitList.unitType}
          type={openUnitList.unitType}
          a11yStandard={a11yStandard}
          property={property}
          units={unitsByType[openUnitList.unitType]}
          propertyImages={propertyImages}
          fullscreen={fullscreen}
          displayShops={displayUnitRooms(unitsByType[openUnitList.unitType])}
          buildingAreaId={buildingAreaId}
        />
      )}
    </RootDiv>
  );
};